import $ from "jquery";
import * as preact from "preact";

import { type PreactPageProps } from "pages/PreactPage";
import readlangClient from "readlangClient";
import { userProfilePath } from "shared/usernameValidation";
import { simpleMarkupToJsx } from "simpleMarkupJsx";
import BookCollectionStore from "stores/BookCollectionStore";
import { shelfViewJSON, type ShelfData } from "stores/ShelvesStore";
import stores from "stores/stores";
import { PublicLibraryList } from "views/PublicLibraryListView";

interface PublicShelfPageOptions {
  shelfID: string;
}

interface PublicShelfUser {
  _id: string;
  username: string;
}

interface PublicShelf extends ShelfData {
  bookCount: number;
  descriptionHtml?: string;
  textsString: string;
  userID: PublicShelfUser;
}

interface State {
  books?: BookCollectionStore;
  error?: string;
  shelf?: PublicShelf;
}

export default class PublicShelfPageView extends preact.Component<
  PreactPageProps,
  State
> {
  state: State = {};
  private mounted = false;
  private disposeLanguageWatch?: () => void;

  componentDidMount() {
    this.mounted = true;
    this.disposeLanguageWatch = stores.user.onChange(
      (user) => user.currentLearningLanguage,
      () => {
        void this.loadShelf();
      }
    );
    void this.loadShelf();
  }

  componentDidUpdate(previousProps: PreactPageProps) {
    if (
      this.options.shelfID !==
      (previousProps.options as PublicShelfPageOptions).shelfID
    ) {
      void this.loadShelf();
    }
  }

  componentWillUnmount() {
    this.mounted = false;
    this.disposeLanguageWatch?.();
  }

  private get options() {
    return this.props.options as PublicShelfPageOptions;
  }

  private async loadShelf() {
    localStorage.setItem(
      "libraryOrDashboardPath",
      "/shelf/" + this.options.shelfID
    );
    this.setState({ books: undefined, error: undefined, shelf: undefined });

    try {
      const [shelf, books] = await Promise.all([
        this.fetchShelf(),
        this.fetchBooks(),
      ]);

      if (!this.mounted) {
        return;
      }

      $("title").text(shelf.name + " - Readlang");
      $('meta[name="description"]').text(shelf.description ?? "");

      if (
        shelf.language &&
        stores.user.currentLearningLanguage.value !== shelf.language
      ) {
        stores.user.save({ currentLearningLanguage: shelf.language });
      }

      this.setState({
        books,
        shelf,
      });
    } catch (error) {
      console.error(error);
      if (this.mounted) {
        this.setState({
          // `reject` doubles as readlangClient's error callback below, so a
          // terminal failure (network loss, 5xx) arrives here already
          // notified — don't claim the shelf itself is gone.
          error: (error as any)?.alreadyNotified
            ? "Couldn't load this shelf."
            : "Shelf not found or is no longer public.",
        });
      }
    }
  }

  private async fetchShelf(): Promise<PublicShelf> {
    return new Promise((resolve, reject) => {
      readlangClient.request(
        "GET",
        "/api/shelf/" + this.options.shelfID,
        {},
        (result) => {
          const shelf = shelfViewJSON(result) as PublicShelf;
          shelf.bookCount = shelf.books.length;
          shelf.textsString = shelf.bookCount === 1 ? "text" : "texts";
          resolve(shelf);
        },
        reject
      );
    });
  }

  private async fetchBooks(): Promise<BookCollectionStore> {
    return new Promise((resolve, reject) => {
      readlangClient.request(
        "GET",
        "/api/books",
        {
          shelfID: this.options.shelfID,
        },
        (books) => {
          resolve(new BookCollectionStore(books));
        },
        reject
      );
    });
  }

  render() {
    if (this.state.error) {
      return <>{this.state.error}</>;
    }

    return (
      <>
        {this.state.shelf && (
          <>
            <h2 style="margin:30px 0;">
              {this.state.shelf.name}
              <br />
              <small>
                (shared by{" "}
                <a
                  href={userProfilePath(
                    this.state.shelf.userID.username,
                    this.state.shelf.userID._id
                  )}
                >
                  {this.state.shelf.userID.username}
                </a>
                , contains {this.state.shelf.bookCount}{" "}
                {this.state.shelf.textsString})
              </small>
            </h2>

            <p style="margin:30px 0;">
              {simpleMarkupToJsx(this.state.shelf.description)}
            </p>
          </>
        )}

        {this.state.books && <PublicLibraryList books={this.state.books} />}

        <div style="clear:both;margin-bottom:20px" />
      </>
    );
  }
}
